import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import axios from 'axios';
import { Button, IconButton } from '@material-ui/core';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';

import EnhancedTableHead, {
  Transition,
  getComparator,
  stableSort,
} from '../../components/Common/EnhancedTableHead';
import AddEnglishWordDialog from './AddEnglisWordDialog';
import { Delete, Edit, Visibility } from '@material-ui/icons';
import DeleteDailog from '../../components/Common/DeleteDailog';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { baseUrl, languageId } from '../../helpers/EncryptionHelper';
import LoadIndicator from '../../components/Common/LoadIndicator';
import CustomSearchBar from '../../components/Common/CustomSearchBar';

const headCells = [
  {
    id: 'english_word_name',
    numeric: false,
    disablePadding: false,
    label: 'English Word Name',
  },
  {
    id: 'partsOfSpeech',
    numeric: false,
    disablePadding: false,
    label: 'Parts of Speech',
  },
  {
    id: 'actions',
    numeric: false,
    disablePadding: false,
    label: 'Actions',
    align: 'center',
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

// process.env.REACT_APP_SERVER_URL + process.env.REACT_APP_BACKEND_ROUTE;

export const globalPosCache = {};

const PosNamesRenderer = ({ partsOfSpeech, wordTypeOptions }) => {
  const [names, setNames] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchNames = async () => {
      if (!partsOfSpeech || partsOfSpeech.length === 0) return;

      const results = new Set();
      // Collect all unique IDs from the partsOfSpeech array
      const ids = partsOfSpeech
        .map((pos) => {
          // Handle various ID formats from different API response versions
          const posObj = pos.languagePartOfSpeech;
          return posObj?._id || posObj?.id || (typeof posObj === 'string' ? posObj : null);
        })
        .filter((id) => id);

      for (const id of ids) {
        if (globalPosCache[id]) {
          results.add(globalPosCache[id]);
        } else {
          // Check if it exists in the pre-loaded wordType options
          const localMatch = (wordTypeOptions || []).find((opt) => opt.value === id);
          if (localMatch) {
            globalPosCache[id] = localMatch.label;
            results.add(localMatch.label);
          } else {
            // Lazy-load name from API if not in cache or options
            try {
              const res = await axios.get(`${baseUrl}/language-part-of-speech/${id}`);
              const fetchedName = res.data?.data?.partOfSpeech?.name || res.data?.data?.name;
              if (fetchedName) {
                globalPosCache[id] = fetchedName;
                results.add(fetchedName);
              }
            } catch (e) {
              console.error('Error fetching POS definition for ID:', id, e);
            }
          }
        }
      }
      if (isMounted) setNames(Array.from(results));
    };
    fetchNames();
    return () => {
      isMounted = false;
    };
  }, [partsOfSpeech, wordTypeOptions]);

  if (names.length === 0) return <span>N/A</span>;
  return <span>{names.join(', ')}</span>;
};

export default function CustomTableEnglishWord() {
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openDailog, setOpenDailog] = useState(false);
  const [openDeleteDailog, setOpenDeleteDailog] = useState(false);
  const [selectedEnglishWord, setselectedEnglishWord] = useState(false);

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [wordType, setWordType] = useState([]);

  const [editEnglishWord, setEditEnglishWord] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClose = () => {
    setselectedEnglishWord();
    setEditEnglishWord(false);
    setOpenDailog(false);
  };

  function handleCloseDeleteDailog() {
    setselectedEnglishWord();
    setOpenDeleteDailog(false);
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // setOffset((s) => s + limit)
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);

    setLimit(parseInt(event.target.value, 10));
    setOffset(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleDelete = async (id) => {
    axios
      .delete(baseUrl + `/english-word/remove/${id}`)
      .then((res) => {
        if (res && res.data.status === 200) {
          handleCloseDeleteDailog();
          toastr.success('English Word Deleted Successfully', 'Success');
          fetchData(offset, limit);
        }
      })
      .catch((err) => {
        toastr.error(`English Word removal Failed`, 'Error!');
      });
  };

  const handleCloseAndRefresh = () => {
    setOpenDailog(false);
    setSelected([]);
    setTimeout(() => {
      fetchData(0, limit);
      setOffset(0);
    }, 200);
  };

  const fetchData = async (offset, limit) => {
    axios
      .get(
        baseUrl +
        `/english-word/all?limit=${limit}&offset=${offset}&language=${languageId}`
      )
      .then((res) => {
        if (res && res.data.status === 200) {
          setRows(res.data.data.list || []);
          setTotalCount(res.data.data.totalCount || 0);
        }
      })
      .catch((err) => {
        console.log('error');
      });
  };

  const fetchWordType = async () => {
    axios
      .get(baseUrl + `/language-part-of-speech?language=${languageId}&limit=1000`)
      .then((res) => {
        if (res && res.data.status === 200) {
          const list =
            res.data.data.languagePartOfSpeeches || res.data.data.list || [];
          const options = list.map((d) => ({
            value: d._id,
            label: d.partOfSpeech?.name || d.name,
          }));
          setWordType(options);

          // Populate cache with what we just fetched to speed up rendering
          options.forEach(opt => {
            if (opt.value && opt.label) {
              globalPosCache[opt.value] = opt.label;
            }
          });

          setShowLoader(false);
        }
      })
      .catch((err) => {
        console.log('error');
        setShowLoader(false);
      });
  };

  useEffect(() => {
    fetchData(offset, limit);
  }, [limit, offset]);

  useEffect(() => {
    fetchWordType();
  }, []);

  const ITEMS_PER_PAGE = 10; // Number of items to display per page
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handleChange = (event, value) => {
    setCurrentPage(value);
    setOffset((value - 1) * limit);
  };

  const currentData = rows || [];

  return (
    <div className={classes.root}>
      <Button
        style={{
          backgroundColor: '#257b59',
          color: 'white',
          padding: 10,
          alignSelf: 'flex-end',
          marginBottom: 20,
        }}
        onClick={() => {
          setselectedEnglishWord();
          setOpenDailog(true);
        }}
      >
        Add English Word
      </Button>
      <Paper className={classes.paper}>
        {showLoader ? (
          <LoadIndicator />
        ) : (
          <>
            <CustomSearchBar
              rows={rows}
              setRows={setRows}
              keysToSearch={['english_word_name']}
            />
            <TableContainer>
              <Table
                className={classes.table}
                aria-labelledby='tableTitle'
                size={dense ? 'small' : 'medium'}
                aria-label='enhanced table'
              >
                <EnhancedTableHead
                  classes={classes}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rows.length}
                  headCells={headCells}
                />
                <TableBody>
                  {currentData.map((row, index) => {
                    const partsOfSpeech = (row.translations || []).flatMap(
                      (t) => t.partsOfSpeech || []
                    );

                    return (
                      <TableRow hover tabIndex={-1} key={row?._id || index}>
                        <TableCell align='left'>
                          {row.english_word_name}
                        </TableCell>
                        <TableCell align='left'>
                          <PosNamesRenderer
                            partsOfSpeech={partsOfSpeech}
                            wordTypeOptions={wordType}
                          />
                        </TableCell>
                        <TableCell width={'30%'} align='center'>
                          <IconButton
                            variant='contained'
                            style={{ color: '#ddd' }}
                            onClick={() => {
                              setselectedEnglishWord(row);
                              setOpenDailog(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton
                            variant='contained'
                            style={{ color: '#ddd' }}
                            onClick={() => {
                              setEditEnglishWord(true);
                              setselectedEnglishWord(row);
                              setOpenDailog(true);
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            variant='contained'
                            style={{ color: '#ddd' }}
                            onClick={() => {
                              setselectedEnglishWord(row);
                              setOpenDeleteDailog(true);
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component='div'
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            /> */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Stack spacing={2}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handleChange}
                  color="primary"
                  variant="outlined" shape="rounded"
                />
              </Stack>
            </div>
          </>
        )}
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label='Dense padding'
      />

      {openDailog && (
        <AddEnglishWordDialog
          data={selectedEnglishWord}
          wordTypeArray={wordType}
          visible={openDailog}
          handleClose={handleClose}
          Transition={Transition}
          editEnglishWord={editEnglishWord}
        />
      )}
      {openDeleteDailog && (
        <DeleteDailog
          data={selectedEnglishWord}
          visible={openDeleteDailog}
          handleClose={handleCloseDeleteDailog}
          Transition={Transition}
          dialogTitle={() => (
            <div className='mt-3'>
              Do you want to delete this English Word :{' '}
              <strong>{selectedEnglishWord?.english_word_name || 'NA'}</strong>
            </div>
          )}
          handleDeleteClicked={(data) =>
            handleDelete(data?.english_word_id || data?._id)
          }
        />
      )}
    </div>
  );
}
